import { useState, useCallback, useEffect } from 'react';

const STORAGE_PREFIX = 'adm_col_vis__';

/**
 * useColumnVisibility
 * Manages per-table column visibility state, persisted to localStorage.
 *
 * @param {string} tableKey   - unique key used as localStorage suffix
 * @param {Array}  allColumns - [{ key: string, title: string }]
 */
export const useColumnVisibility = (tableKey, allColumns) => {
  const storageKey = STORAGE_PREFIX + (tableKey || 'default');

  const buildDefaultVisibilityMap = useCallback(() => {
    const initial = {};
    if (Array.isArray(allColumns)) {
      allColumns.forEach((col) => {
        if (col && typeof col.key === 'string' && col.key.trim() !== '') {
          initial[col.key] = true;
        }
      });
    }
    return initial;
  }, [allColumns]);

  const [visibilityMap, setVisibilityMap] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed;
        }
      }
    } catch (e) {
      // ignore parse errors
    }
    // Default: all columns visible
    return buildDefaultVisibilityMap();
  });

  // Ensure newly introduced columns appear in visibility state as visible.
  useEffect(() => {
    const defaults = buildDefaultVisibilityMap();
    setVisibilityMap((prev) => {
      const next = { ...defaults, ...prev };
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next || {});
      if (prevKeys.length === nextKeys.length) {
        let changed = false;
        for (let i = 0; i < nextKeys.length; i += 1) {
          const k = nextKeys[i];
          if (prev[k] !== next[k]) {
            changed = true;
            break;
          }
        }
        if (!changed) {
          return prev;
        }
      }
      return next;
    });
  }, [buildDefaultVisibilityMap]);

  // Persist whenever visibility changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(visibilityMap));
    } catch (e) {
      // ignore storage errors
    }
  }, [visibilityMap, storageKey]);

  const toggleColumn = useCallback((key) => {
    setVisibilityMap(prev => ({
      ...prev,
      [key]: prev[key] === false ? true : false,
    }));
  }, []);

  const setAllColumnsVisible = useCallback((visible) => {
    setVisibilityMap((prev) => {
      const next = { ...prev };
      if (Array.isArray(allColumns)) {
        allColumns.forEach((col) => {
          if (col && typeof col.key === 'string' && col.key.trim() !== '') {
            next[col.key] = visible;
          }
        });
      }
      return next;
    });
  }, [allColumns]);

  const resetColumnVisibility = useCallback(() => {
    setVisibilityMap(buildDefaultVisibilityMap());
  }, [buildDefaultVisibilityMap]);

  // Returns true if column is visible (default: true for unknown keys)
  const isColumnVisible = useCallback((key) => {
    return visibilityMap[key] !== false;
  }, [visibilityMap]);

  // Merge saved visibility with current column list
  const columnsWithVisibility = Array.isArray(allColumns)
    ? allColumns.map(col => ({ ...col, visible: isColumnVisible(col.key) }))
    : [];

  return {
    visibilityMap,
    columnsWithVisibility,
    toggleColumn,
    isColumnVisible,
    setAllColumnsVisible,
    resetColumnVisibility,
  };
};
